import { CdkDragEnter, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnInit,
  QueryList,
  Renderer2,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { combineLatest, filter } from 'rxjs';

import { BoardsAbstract } from '@main-application/boards/abstract/board-abstract.component';
import { CustomInspectionHeaderComponent } from '@main-application/boards/boards-view/boards-cell/cell-types/schedule-cell/custom-inspection-template-dialog/custom-inspection-header/custom-inspection-header.component';
import { CustomInspectionItemComponent } from '@main-application/boards/boards-view/boards-cell/cell-types/schedule-cell/custom-inspection-template-dialog/custom-inspection-item/custom-inspection-item.component';
import { BoardColumn, RestTurnStepScheduleModel } from '@main-application/boards/interfaces/board';
import { ColumnSettings } from '@main-application/boards/interfaces/boards-configuration';
import {
  CustomInspectionContent,
  RestCustomInspectionContentItemModel,
} from '@main-application/boards/interfaces/custom-inspection';
import { BoardEventService } from '@main-application/boards/services/board-event.service';
import { CustomInspectionService } from '@main-application/boards/services/custom-inpection.service';
import { ButtonType } from '@shared/enums/button-type';
import { EIcon } from '@shared/enums/icon.enum';
import { ConfirmationModalData } from '@shared/interfaces/modal-data';
import { RestTurnoverTaskModel } from '@shared/interfaces/turnover.interface';
import { RestTemplateModel } from '@template/models/rest-template-model.interface';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';
import { DialogResult } from '@ui-components/modals/config/dialog-result.enum';
import { ModalsService } from '@ui-components/modals/modals.service';

export type CustomInspectionDialogData = {
  turnover?: RestTurnoverTaskModel;
  column?: BoardColumn;
  boardEventService: BoardEventService;
  highLightHeader?: boolean;
} & (
  | {
      cellData: RestTurnStepScheduleModel;
      inspectionTemplateId?: never;
    }
  | {
      inspectionTemplateId: number;
      cellData?: never;
    }
);

export interface CustomInspectionDialogResult {
  inspectionTemplateId?: number;
  inspectionTemplateName?: string;
}

@UntilDestroy()
@Component({
  selector: 'app-custom-inspection-template-dialog',
  templateUrl: './custom-inspection-dialog.component.html',
  styleUrls: ['./custom-inspection-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomInspectionDialogComponent extends BoardsAbstract implements OnInit, AfterViewInit {
  ButtonType = ButtonType;
  EIcon = EIcon;
  inspectionContent: CustomInspectionContent = new CustomInspectionContent();
  inspectionTemplateContent: CustomInspectionContent = new CustomInspectionContent();
  columnConfiguration: ColumnSettings;
  isBlockDraggable = true;
  isTemplateMode = false;

  @ViewChild('addButtonContainer', { read: ElementRef, static: false })
  addColumnButtonContainer: ElementRef<HTMLElement>;
  isLoading = true;
  isItemsLoading = true;
  isUnitModeAvailable = true;
  showSwitcher = true;
  //templateChanged = false;
  doNotShowSaveTemplateDialogOnClose: boolean;
  inspectionTemplate: RestTemplateModel;

  target?: CdkDropList;
  targetIndex?: number;
  source?: CdkDropList;
  sourceIndex?: number;
  @ViewChild(CdkDropList) placeholder: CdkDropList;
  @ViewChildren(CustomInspectionItemComponent) inspectionItems!: QueryList<CustomInspectionItemComponent>;
  @ViewChild(CustomInspectionHeaderComponent) header: CustomInspectionHeaderComponent;

  constructor(
    public dialogRef: MatDialogRef<CustomInspectionDialogComponent, CustomInspectionDialogResult>,
    protected store: Store<{}>,
    protected cdr: ChangeDetectorRef,
    private renderer: Renderer2,
    private customInspectionService: CustomInspectionService,
    private modalsService: ModalsService,
    protected snackbarService: SnackbarService,
    @Inject(MAT_DIALOG_DATA) public data: CustomInspectionDialogData
  ) {
    super(store, snackbarService);
  }

  ngOnInit(): void {
    let inspectionTemplateId: number;
    let inspectionId: number;
    if (this.data?.cellData?.inspectionId) {
      this.data.boardEventService.getBoardConfig().subscribe(boardConfig => {
        this.columnConfiguration = boardConfig.columnSettings[this.data.column.id];
        this.doNotShowSaveTemplateDialogOnClose = !!this.columnConfiguration?.doNotShowSaveTemplateDialogOnClose;
      });
      inspectionTemplateId = this.data.cellData.inspectionTemplateId;
      inspectionId = this.data.cellData.inspectionId;
    } else {
      inspectionTemplateId = this.data.inspectionTemplateId ?? this.data.column.inspectionTemplateId;
      this.isTemplateMode = true;
      this.isUnitModeAvailable = false;
    }

    combineLatest([
      this.customInspectionService.getInspectionTemplate(inspectionTemplateId),
      this.customInspectionService.getCustomInspectionTemplateContent(inspectionTemplateId),
      ...(this.isUnitModeAvailable ? [this.customInspectionService.getCustomInspectionContent(inspectionId)] : []),
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([inspectionTemplate, inspectionTemplateContent, inspectionContent]) => {
        if (this.isUnitModeAvailable) {
          this.inspectionContent.init(inspectionContent);
          this.inspectionContent.itemsChanges$.subscribe(items => {
            this.customInspectionService
              .updateCustomInspectionContent(inspectionId, items)
              .pipe(untilDestroyed(this))
              .subscribe();
          });
        }
        this.inspectionTemplate = inspectionTemplate;
        if (!inspectionTemplate.isAvailable) {
          this.showSwitcher = false;
        }
        this.inspectionTemplateContent.init(inspectionTemplateContent);
        this.inspectionTemplateContent.itemsChanges$.subscribe(() => {
          this.customInspectionService
            .updateCustomInspectionTemplateContent(inspectionTemplateId, this.inspectionTemplateContent.templateItems)
            .pipe(untilDestroyed(this))
            .subscribe();
        });
        this.isLoading = false;
        this.isItemsLoading = false;
        this.cdr.markForCheck();
      });

    // this.inspectionContent.templateChanges$.subscribe(() => {
    //   this.templateChanged = true;
    // });

    this.dialogRef.backdropClick().subscribe(() => {
      this.cancel();
    });

    this.dialogRef.keydownEvents().subscribe(event => {
      if (event.key === 'Escape') {
        this.cancel();
      }
    });
  }

  get activeInspectionContent() {
    return this.isTemplateMode ? this.inspectionTemplateContent : this.inspectionContent;
  }

  changeMode(value: boolean) {
    this.isItemsLoading = true;
    this.isTemplateMode = value;
    this.cdr.markForCheck();
    setTimeout(() => {
      this.isItemsLoading = false;
      this.cdr.markForCheck();
    }, 300);
  }

  trackById(_index: number, item?: RestCustomInspectionContentItemModel) {
    return item?.inspectionItemId;
  }

  openSaveTemplateDialog(onClose = false) {
    return this.modalsService
      .openCustomInspectionSaveTemplateModal({
        inspectionTemplate: {
          inspectionName: this.inspectionTemplate.name,
          isRequired: this.inspectionTemplate.customInspectionIsRequired,
          id: this.inspectionTemplate.id,
        },
        showDoNotShowAgain: onClose,
        inspectionTemplateContent: this.inspectionContent.templateItems,
      })
      .afterClosed();
  }

  add() {
    const customNamedItemMaxNumber = this.activeInspectionContent.items
      .filter(e => /^Item [0-9]*$/.test(e.inspectionItemName))
      .map(c => +c.inspectionItemName.replace('Item ', ''))
      .reduce((prev, max) => (prev > max ? prev : max), 0);

    const id = this.activeInspectionContent.addItem(`Item ${customNamedItemMaxNumber + 1}`);
    this.cdr.markForCheck();
    setTimeout(() => {
      const item = this.inspectionItems.find(c => c.id === id);
      item?.enterEditMode();
      this.addColumnButtonContainer.nativeElement.scrollIntoView();
    }, 100);
  }

  enterPressed(item: RestCustomInspectionContentItemModel) {
    const index = this.activeInspectionContent.items.findIndex(
      itemInner => itemInner.inspectionItemId === item.inspectionItemId
    );
    if (index === this.activeInspectionContent.items.length - 1) {
      this.add();
    } else {
      const id = this.activeInspectionContent.items[index + 1].inspectionItemId;
      const selectedItem = this.inspectionItems.find(c => c.id === id);
      selectedItem?.enterEditMode();
    }
  }

  remove(item: RestCustomInspectionContentItemModel) {
    if (!this.checkEditRight()) {
      return;
    }
    this.activeInspectionContent.removeItem(item.inspectionItemId);
  }

  itemNameChange(item: RestCustomInspectionContentItemModel) {
    if (!this.checkEditRight()) {
      return;
    }
    this.activeInspectionContent.updateItemName(item);
  }

  removeTemplate() {
    if (!this.checkEditRight()) {
      return;
    }
    const data: ConfirmationModalData = {};
    if (!this.isTemplateMode) {
      data.title = `Delete inspection ${this.inspectionTemplate.name}?`;
      data.content = `This will delete inspection. Are you feeling this confident?`;
    }
    this.customInspectionService
      .openRemovalTemplateModal(this.inspectionTemplate, data)
      .afterClosed()
      .pipe(filter(result => result === DialogResult.Success))
      .subscribe(() => {
        if (this.isTemplateMode) {
          if (!this.isUnitModeAvailable) {
            this.dialogRef.close();
          }
          this.customInspectionService.hideInspectionTemplate(this.inspectionTemplate).subscribe();
          this.isTemplateMode = false;
          this.showSwitcher = false;
          this.cdr.markForCheck();
        } else {
          this.dialogRef.close({ inspectionTemplateId: null });
        }
      });
  }

  itemCheckedChange(item: RestCustomInspectionContentItemModel) {
    this.activeInspectionContent.updateItemChecked(item, { emitChange: !this.isTemplateMode });
    if (this.isTemplateMode) {
      this.snackbarService.error('Items cannot be completed in Template mode');
      setTimeout(() => {
        item.inspectionItemChecked = false;
        this.activeInspectionContent.updateItemChecked(item, { emitChange: !this.isTemplateMode });
        this.cdr.markForCheck();
      });
    }
  }

  headerChange(header: string) {
    this.inspectionTemplate.name = header;
    if (this.data.cellData) {
      this.modalsService
        .openConfirmationModal(
          {
            title: 'Save template?',
            content: 'Do you want to change current template name or create new?',
            cancelColor: 'primary',
            confirmColor: 'primary',
            cancelLabel: 'Save as existing',
            confirmLabel: 'Save as new',
          },
          360
        )
        .afterClosed()
        .subscribe(result => {
          if (result === DialogResult.Success) {
            this.createNewTemplate();
          } else {
            this.updateCurrentTemplate();
          }
        });
    } else {
      this.updateCurrentTemplate();
    }
  }

  createNewTemplate() {
    this.customInspectionService
      .addCustomInspectionTemplateWithItems(
        {
          inspectionName: this.inspectionTemplate.name,
          isRequired: this.inspectionTemplate.customInspectionIsRequired,
        },
        this.inspectionTemplateContent.templateItems
      )
      .subscribe(([templateId]) => {
        this.dialogRef.close({
          inspectionTemplateId: templateId,
          inspectionTemplateName: this.inspectionTemplate.name,
        });
      });
  }

  updateCurrentTemplate() {
    this.customInspectionService
      .editCustomInspectionTemplate(this.inspectionTemplate.id, {
        inspectionName: this.inspectionTemplate.name,
        isRequired: this.inspectionTemplate.customInspectionIsRequired,
      })
      .subscribe(() => {
        if (this.data.column && this.data.column.name !== this.inspectionTemplate.name) {
          const board = this.data.boardEventService.getBoard();
          const currentColumn = board.columns.find(column => column.id === this.data.column.id);
          currentColumn.name = this.inspectionTemplate.name;
          this.data.boardEventService.save();
        }
      });
  }

  ngAfterViewInit() {
    const phElement = this.placeholder.element.nativeElement;

    this.renderer.setStyle(phElement, 'display', 'none');
    this.renderer.removeChild(phElement.parentNode, phElement);

    if (this.data.highLightHeader) {
      setTimeout(() => {
        this.header?.enterEditMode();
      }, 0);
    }
  }
  drop() {
    if (!this.checkEditRight()) {
      return;
    }
    if (!this.target) return;

    const phElement = this.placeholder.element.nativeElement;
    const parent = phElement.parentNode;

    this.renderer.setStyle(phElement, 'display', 'inline');

    this.renderer.removeChild(parent, phElement);

    this.renderer.insertBefore(parent, this.source.element.nativeElement, parent.children[this.sourceIndex]);

    if (this.sourceIndex != this.targetIndex) {
      moveItemInArray(this.activeInspectionContent.items, this.sourceIndex, this.targetIndex);
    }

    this.target = undefined;
    this.source = undefined;
    this.targetIndex = undefined;
    this.sourceIndex = undefined;

    this.activeInspectionContent.onItemChanged();
    this.cdr.detectChanges();
  }

  enter(e: CdkDragEnter) {
    if (!this.checkEditRight()) {
      return;
    }
    const drag = e.item;
    const drop = e.container;

    if (drop === this.placeholder) return;

    const phElement = this.placeholder.element.nativeElement;
    const dropElement = drop.element.nativeElement;

    const dragIndex =
      typeof this.sourceIndex === 'number'
        ? this.sourceIndex
        : __indexOf(dropElement.parentNode.children, drag.dropContainer.element.nativeElement);

    const dropIndex = __indexOf(dropElement.parentNode.children, dropElement);

    if (!this.source) {
      this.sourceIndex = dragIndex;
      this.source = drag.dropContainer;
      const sourceElement = this.source.element.nativeElement;

      this.renderer.removeChild(sourceElement.parentNode, sourceElement);
    }

    this.renderer.setStyle(phElement, 'display', '');

    if (this.sourceIndex < dropIndex) {
      if (dropIndex < this.targetIndex) {
        this.renderer.insertBefore(dropElement.parentElement, phElement, dropElement);
      } else {
        this.renderer.insertBefore(dropElement.parentElement, phElement, dropElement.nextSibling);
      }
    } else {
      if (this.targetIndex < dropIndex) {
        this.renderer.insertBefore(dropElement.parentElement, phElement, dropElement.nextSibling);
      } else {
        this.renderer.insertBefore(dropElement.parentElement, phElement, dropElement);
      }
    }

    this.targetIndex = dropIndex;
    this.target = drop;

    this.placeholder._dropListRef.enter(
      drag._dragRef,
      drag.element.nativeElement.offsetLeft,
      drag.element.nativeElement.offsetTop
    );

    this.cdr.detectChanges();
  }

  cancel() {
    // TODO: commented for MVP
    // if (this.templateChanged && !this.doNotShowSaveTemplateDialogOnClose) {
    //   this.openSaveTemplateDialog(true).subscribe(result => {
    //     if (result.doNotShowAgain) {
    //       this.data.boardEventService.updateColumnStore(this.data.column.id, {
    //         doNotShowSaveTemplateDialogOnClose: result.doNotShowAgain,
    //       });
    //     }
    //     this.dialogRef.close();
    //   });
    // } else {
    this.dialogRef.close();
    //}
  }
}

function __indexOf(collection: any, node: any) {
  return Array.prototype.indexOf.call(collection, node);
}
